import React from 'react';
import { FaExclamationTriangle } from 'react-icons/fa';
import { usePhaserKeyboardSuspend } from '../../hooks/usePhaserKeyboardSuspend';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
}

export const Input: React.FC<InputProps> = ({ 
  label, 
  error, 
  className = '',
  id,
  ...props 
}) => {
  const inputId = id || props.name;
  const phaserKeyboard = usePhaserKeyboardSuspend();

  // Suspend Phaser keyboard while typing
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    phaserKeyboard.onFocus();
    // Call original onFocus if provided
    props.onFocus?.(e);
  };

  // Restore Phaser keyboard when leaving the field
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    phaserKeyboard.onBlur();
    // Call original onBlur if provided
    props.onBlur?.(e);
  };

  return (
    <div className="mb-4">
      {label && (
        <label htmlFor={inputId} className="block text-sm font-semibold text-white mb-2">
          {label}
        </label>
      )}
      <input
        id={inputId}
        className={`w-full px-4 py-3 bg-game-cream border border-game-medium/50 rounded-lg text-gray-900 placeholder-gray-600 focus:border-game-golden focus:bg-game-cream/90 focus:shadow-lg focus:shadow-game-golden/20 focus:outline-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${error ? 'border-game-orange bg-game-orange/20' : ''} ${className}`.trim()}
        {...props}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
      {error && (
        <div className="mt-2 p-2 bg-game-orange/20 border border-game-orange/50 text-game-accent rounded-md text-sm flex items-center gap-2">
          <FaExclamationTriangle className="text-game-golden flex-shrink-0" />
          {error}
        </div>
      )}
    </div>
  );
};

interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
}

export const TextArea: React.FC<TextAreaProps> = ({ 
  label, 
  error, 
  className = '',
  id,
  ...props 
}) => {
  const inputId = id || props.name;
  const phaserKeyboard = usePhaserKeyboardSuspend();

  // Suspend Phaser keyboard while typing
  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    phaserKeyboard.onFocus();
    // Call original onFocus if provided
    props.onFocus?.(e);
  };

  // Restore Phaser keyboard when leaving the field
  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    phaserKeyboard.onBlur();
    // Call original onBlur if provided
    props.onBlur?.(e);
  };

  return (
    <div className="mb-4">
      {label && (
        <label htmlFor={inputId} className="block text-sm font-semibold text-white mb-2">
          {label}
        </label>
      )}
      <textarea
        id={inputId}
        className={`w-full px-4 py-3 bg-game-cream border border-game-medium/50 rounded-lg text-gray-900 placeholder-gray-600 focus:border-game-golden focus:bg-game-cream/90 focus:shadow-lg focus:shadow-game-golden/20 focus:outline-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed resize-vertical ${error ? 'border-game-orange bg-game-orange/20' : ''} ${className}`.trim()}
        {...props}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
      {error && (
        <div className="mt-2 p-2 bg-game-orange/20 border border-game-orange/50 text-game-accent rounded-md text-sm flex items-center gap-2">
          <FaExclamationTriangle className="text-game-golden flex-shrink-0" />
          {error}
        </div>
      )}
    </div>
  );
};